import AppContext from 'App/AppContext';
import { IPActionButton, IPFontIcon, IPLabel, IPText } from 'baseComponents';
import { IPDetailsListLayoutMode } from 'baseComponents/DetailsList/IPDetailsList';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPSelectionMode } from 'baseComponents/Utils/Selection';
import {
  deleteIcon,
  globalStackStylesHeight100,
  globalStackTokensGapExtraSmall,
  globalStackTokensGapMedium,
  globalStackTokensGapSmall,
  newIcon,
} from 'globalStyles';
import Task, { TaskTypes } from 'models/tasks/task';
import { addTaskContextFrom } from 'models/tasks/taskContext';
import { Fragment, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { newGuid } from 'utils/guid';
import SingleTask from '../SingleTask';
import SubTaskList from '../SubTaskList';
import { iconFontSize } from './Details/TaskDetailStyles';

interface IEventSubTasksProps {
  task: Task;
  isActionPending: boolean;
  windowLevel?: number;
  navigateExternal?: boolean;
  //the new view has a column of its own for the list: no icon in front of the label
  hideIcon?: boolean;
  onUpdate: (newTask: Task) => void;
}

// The subtasks of an event series: label, add/remove actions, the list and the subtask dialogs
export const EventSubTasks = (props: IEventSubTasksProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['task', 'tasks']);
  const [newSubTask, setNewSubTask] = useState<Task | undefined>(undefined);
  const [editSubTask, setEditSubTask] = useState<Task | undefined>(undefined);
  const [selectedSubTasks, setSelectedSubTasks] = useState<Task[]>([]);
  const [isCreatingSubTask, setIsCreatingSubTask] = useState<boolean>(false);

  const addSubTask = async () => {
    let newSubTask = new Task();
    newSubTask.taskType = TaskTypes.Event;
    newSubTask.taskTypeId = props.task.taskTypeId;
    newSubTask.taskMasterId = props.task.taskId;
    newSubTask.taskStates = appContext.globalDataCache.taskStates.items;
    newSubTask.taskStateId = newSubTask.getFirstState();
    newSubTask.id = newGuid();
    newSubTask.creatorId = appContext.user.id;
    newSubTask.creator = appContext.user;
    newSubTask.ownerId = props.task.ownerId;
    newSubTask.owner = props.task.owner;
    newSubTask.authSchemaId = props.task.authSchemaId;
    newSubTask.tagIds = [...(props.task.tagIds ?? [])];

    try {
      //add the related entities of the event to the new sub task
      setIsCreatingSubTask(true);
      newSubTask = await addTaskContextFrom(newSubTask, props.task.taskId, appContext);
    } catch (err) {
      appContext.setError(err);

      return;
    } finally {
      setIsCreatingSubTask(false);
    }

    setNewSubTask(newSubTask);
  };

  const onAddSubTask = (task: Task, isNew: boolean, isCancelled: boolean) => {
    if (!isCancelled) {
      if (isNew) {
        const newTask = props.task.clone();
        if (!newTask.instances) newTask.instances = [];
        newTask.instances.push(task);
        props.onUpdate(newTask);
        setNewSubTask(undefined);
      }
    }
  };

  const onEditSubTask = (task: Task, isNew: boolean, isCancelled: boolean) => {
    if (!isCancelled) {
      onUpdateSubTask(task);
    }
  };

  const onRemoveSubTask = (task: Task) => {
    //not implemented, removing is done through the subtask list
  };

  const onUpdateSubTask = (task: Task) => {
    const newTask = props.task.clone();
    if (!newTask.instances) return;
    newTask.instances = newTask.instances.map((i) => (i.id === task.id ? task : i));
    props.onUpdate(newTask);
  };

  const removeSelectedSubTasks = () => {
    const newTask = props.task.clone();
    if (!newTask.instances) return;
    newTask.instances = newTask.instances.filter(
      (instance) => !selectedSubTasks.some((selectedSubTask) => selectedSubTask.id === instance.id),
    );
    props.onUpdate(newTask);
    if (newTask.instances.length === 0) {
      setSelectedSubTasks([]);
    }
  };

  return (
    <Fragment>
      <IPStack.Item>
        <IPStack
          horizontal
          tokens={appContext.isMobileView ? globalStackTokensGapExtraSmall : globalStackTokensGapMedium}
          verticalAlign="start"
          styles={globalStackStylesHeight100}
        >
          {!props.hideIcon && (
            <IPStack.Item>
              {!appContext.isMobileView && <IPFontIcon iconName="WorkFlow" style={iconFontSize} />}
            </IPStack.Item>
          )}
          <IPStack.Item>
            <IPStack>
              <IPLabel>{t('task:Subtasks.Label')}</IPLabel>
              {props.task.instancesEnabled && (
                <IPStack horizontal tokens={globalStackTokensGapSmall}>
                  <IPActionButton
                    disabled={isCreatingSubTask}
                    onClick={() => addSubTask()}
                    iconProps={newIcon}
                    text={t('task:Subtasks.AddTask')}
                  />
                  <IPActionButton
                    iconProps={deleteIcon}
                    text={t('tasks:TabAllTasks.Commands.Delete')}
                    disabled={selectedSubTasks.length === 0}
                    onClick={() => removeSelectedSubTasks()}
                  />
                </IPStack>
              )}
            </IPStack>
          </IPStack.Item>
        </IPStack>
      </IPStack.Item>
      {(props.isActionPending || props.task.getShowinstances()) && (
        <IPStack.Item grow styles={props.hideIcon ? { root: { minHeight: 0, overflowY: 'auto' } } : undefined}>
          <SubTaskList
            subTasks={props.task.instances ?? []}
            selectionMode={IPSelectionMode.multiple}
            isLoading={props.isActionPending}
            onSubTaskClick={(task) => {
              setEditSubTask(task);
            }}
            updateSubTasks={(newInstances) => {
              const newTask = props.task.clone();
              newTask.instances = newInstances;
              props.onUpdate(newTask);
            }}
            layoutMode={IPDetailsListLayoutMode.fixedColumns}
            hideColumns={['description']}
            onChangeSelection={setSelectedSubTasks}
          />
        </IPStack.Item>
      )}
      {!props.task.getShowinstances() && !props.isActionPending && (
        <IPStack.Item grow>
          <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
            <IPStack.Item>
              <IPText>{t('task:Subtasks.Disabled')}</IPText>
            </IPStack.Item>
          </IPStack>
        </IPStack.Item>
      )}
      {newSubTask && (
        <SingleTask
          isOpen={newSubTask !== undefined}
          task={newSubTask}
          close={() => setNewSubTask(undefined)}
          onSave={onAddSubTask}
          onRemove={() => setNewSubTask(undefined)}
          onUpdate={onUpdateSubTask}
          windowLevel={(props.windowLevel ?? 0) + 1}
          disallowSave={true}
          navigateExternal={props.navigateExternal}
        />
      )}
      {editSubTask && (
        <SingleTask
          isOpen={editSubTask !== undefined}
          task={editSubTask}
          close={() => setEditSubTask(undefined)}
          onSave={onEditSubTask}
          onRemove={onRemoveSubTask}
          onUpdate={onUpdateSubTask}
          windowLevel={(props.windowLevel ?? 0) + 1}
          disallowSave={true}
          navigateExternal={props.navigateExternal}
        />
      )}
    </Fragment>
  );
};
